import React from 'react';
import PropTypes from 'prop-types';
import { Form, Icon, Popconfirm } from 'antd';
import { autobind } from 'core-decorators';
import moment from 'moment';
import BaseComponent from 'components/BaseComponent';
import styledComp, { FlexColContainer, FlexRowContainer } from 'components/StyledComp';
import CustomButton from 'components/CustomButton';
import DataTables from 'components-desktop/DataTables';
import FormDatePicker from 'components-desktop/FormDatePicker';
import FormTimePicker from 'components-desktop/FormTimePicker';
import FormInput from 'components-desktop/FormInput';
import ChangeHistoriesButton from 'components-desktop/histories/ChangeHistoriesButton';
import helpers from 'misc/helpers';

const propTypes = {
  form: PropTypes.object.isRequired,
};

@autobind
class CaregiverSickLeaves extends BaseComponent {
  constructor(props) {
    super(props);

    this.state = {
      adding: false,
      sickLeaves: [],
    };
  }

  componentDidMount() {
    this.id = this.parseQueryParam('id', this.props.location);
    this.getCaregiverSickLeaves();
  }

  async getCaregiverSickLeaves() {
    const params = { caregiverId: this.id };
    const promise1 = this.callServiceForRemoteObjects('/sick_leave/get_list', params);
    const results = await Promise.all([promise1]);

    this.setState({
      sickLeaves: results[0],
    });
  }

  onAddClick() {
    this.setState({
      adding: true,
    });
  }

  onUpdateClick(record) {
    const validatingFields = [
      `date-${record.id}`,
      `startTime-${record.id}`,
      `endTime-${record.id}`,
      `remark-${record.id}`,
    ];
    this.props.form.validateFieldsAndScroll(validatingFields, async (err, values) => {
      if (!err) {
        const params = {
          id: record.id,
          date: helpers.extraFormDateValue(values, `date-${record.id}`),
          startTime: helpers.extraFormTimeValue(values, `startTime-${record.id}`),
          endTime: helpers.extraFormTimeValue(values, `endTime-${record.id}`),
          remark: helpers.extraFormInputValue(values, `remark-${record.id}`),
        };
        await this.callService('/sick_leave/update', params);

        this.notifySuccess('Success', 'The sick leave record has been updated.');
      }
    });
  }

  async onDeleteClick(record) {
    await this.callService('/sick_leave/delete', { id: record.id });

    this.notifySuccess('Success', 'The sick leave record has been deleted.');

    this.getCaregiverSickLeaves();
  }

  onSaveClick() {
    const validatingFields = [
      'date-0',
      'startTime-0',
      'endTime-0',
      'remark-0',
    ];
    this.props.form.validateFieldsAndScroll(validatingFields, async (err, values) => {
      if (!err) {
        const params = {
          caregiverId: this.id,
          date: helpers.extraFormDateValue(values, 'date-0'),
          startTime: helpers.extraFormTimeValue(values, 'startTime-0'),
          endTime: helpers.extraFormTimeValue(values, 'endTime-0'),
          remark: helpers.extraFormInputValue(values, 'remark-0'),
        };
        await this.callService('/sick_leave/add', params);

        this.notifySuccess('Success', 'A new sick leave record has been added.');

        this.onCancelClick();
        this.getCaregiverSickLeaves();
      }
    });
  }

  onCancelClick() {
    this.setState({
      adding: false,
    });
  }

  onStartTimeChange(startTime, record) {
    const endTime = this.props.form.getFieldValue(`endTime-${record.id}`);
    this.calcAndSetDuration(startTime, endTime, record);
  }

  onEndTimeChange(endTime, record) {
    const startTime = this.props.form.getFieldValue(`startTime-${record.id}`);
    this.calcAndSetDuration(startTime, endTime, record);
  }

  // Calculates the duration between two time. Note that, when the endTime <= startTime,
  // we assume that the endTime has gone to the next day.
  calcAndSetDuration(startTime, endTime, record) {
    if (startTime && endTime) {
      const diffInMs = startTime.isBefore(endTime)
        ? endTime.diff(startTime)
        : 86400000 - (startTime.diff(endTime));

      const duration = moment.duration(diffInMs);
      const hours = Math.floor(duration.asHours());
      const minutes = moment.utc(duration.asMilliseconds()).format('mm');
      const durationString = `${hours < 10 ? '0' : ''}${hours}:${minutes}`;

      this.setState({
        [`duration-${record.id}`]: durationString,
      });
    }
  }

  createColumns() {
    const { form } = this.props;

    return [{
      title: 'Date',
      dataIndex: 'dateUsString',
      sorter: (a, b) => a.date - b.date,
      defaultSortOrder: 'descend',
      render: (text, record) => (
        <FormDatePicker
          width="match_parent"
          height="wrap_content"
          margin="0"
          form={form}
          name={`date-${record.id}`}
          label=""
          placeholder="mm/dd/yyyy"
          initialValue={record.dateUsString && moment(record.dateUsString, 'MM/DD/YYYY')}
          isRequired
        />
      ),
    }, {
      title: 'Start Time',
      dataIndex: 'startTimeString',
      sorter: (a, b) => a.startTime - b.startTime,
      render: (text, record) => (
        <FormTimePicker
          width="match_parent"
          height="wrap_content"
          margin="0"
          form={form}
          name={`startTime-${record.id}`}
          label=""
          placeholder="hh:mm"
          initialValue={record.startTimeString && moment(record.startTimeString, 'HH:mm')}
          isRequired
          onChange={time => this.onStartTimeChange(time, record)}
        />
      ),
    }, {
      title: 'End Time',
      dataIndex: 'endTimeString',
      sorter: (a, b) => a.endTime - b.endTime,
      render: (text, record) => (
        <FormTimePicker
          width="match_parent"
          height="wrap_content"
          margin="0"
          form={form}
          name={`endTime-${record.id}`}
          label=""
          placeholder="hh:mm"
          initialValue={record.endTimeString && moment(record.endTimeString, 'HH:mm')}
          isRequired
          onChange={time => this.onEndTimeChange(time, record)}
        />
      ),
    }, {
      title: 'Duration',
      dataIndex: 'durationString',
      sorter: (a, b) => a.durationString?.localeCompare(b.durationString),
      render: (text, record) => (
        <styledComp.Span
          width="wrap_content"
          height="wrap_content"
          color="textPrimaryDark"
          fontSize="14px"
        >
          {this.state[`duration-${record.id}`] || text}
        </styledComp.Span>
      ),
    }, {
      title: 'Remark',
      dataIndex: 'remark',
      sorter: (a, b) => a.remark?.localeCompare(b.remark),
      render: (text, record) => (
        <FormInput
          width="match_parent"
          height="wrap_content"
          margin="0"
          form={form}
          name={`remark-${record.id}`}
          label=""
          max={2000}
          initialValue={record.remark}
        />
      ),
    }, {
      title: 'Actions',
      render: (text, record) => (
        <FlexRowContainer
          width="match_parent"
          height="wrap_content"
          justifyContent="space-around"
        >
          {record.id !== 0 &&
            <styledComp.A
              width="wrap_content"
              height="wrap_content"
              fontSize="14px"
              onClick={() => this.onUpdateClick(record)}
            >
              Update
            </styledComp.A>
          }

          {record.id !== 0 &&
            <Popconfirm
              title="Are you sure you want to delete this record?"
              placement="top"
              okText="Confirm"
              cancelText="Cancel"
              onConfirm={() => this.onDeleteClick(record)}
            >
              <styledComp.A
                width="wrap_content"
                height="wrap_content"
                fontSize="14px"
              >
                Delete
              </styledComp.A>
            </Popconfirm>
          }

          {record.id === 0 &&
            <styledComp.A
              width="wrap_content"
              height="wrap_content"
              fontSize="14px"
              onClick={this.onSaveClick}
            >
              Save
            </styledComp.A>
          }

          {record.id === 0 &&
            <styledComp.A
              width="wrap_content"
              height="wrap_content"
              fontSize="14px"
              onClick={this.onCancelClick}
            >
              Cancel
            </styledComp.A>
          }
        </FlexRowContainer>
      ),
    }];
  }

  createDataSource(sickLeaves) {
    const dataSource = [];
    sickLeaves.forEach((sickLeave, index) => {
      dataSource.push({
        key: sickLeave.getId(),
        id: sickLeave.getId(),
        date: sickLeave.getLong('date'),
        dateUsString: sickLeave.getString('dateUsString'),
        startTime: sickLeave.getLong('startTime'),
        startTimeString: sickLeave.getString('startTimeString'),
        endTime: sickLeave.getLong('endTime'),
        endTimeString: sickLeave.getString('endTimeString'),
        durationString: sickLeave.getString('durationString'),
        remark: sickLeave.getString('remark'),
      });
    });

    // If adding, add a new empty row at the top.
    if (this.state.adding) {
      dataSource.unshift({
        key: 0,
        id: 0,
        date: null,
        dateUsString: null,
        startTime: null,
        startTimeString: null,
        endTime: null,
        endTimeString: null,
        durationString: null,
        remark: '',
      });
    }

    return dataSource;
  }

  renderContent() {
    const { ...layoutProps } = this.props;
    const { sickLeaves } = this.state;

    return (
      <FlexColContainer
        {...layoutProps}
        padding="20px 20px"
      >
        <FlexRowContainer
          width="match_parent"
          height="wrap_content"
          margin="0 0 20px 0"
          justifyContent="space-between"
          alignItems="center"
        >
          <CustomButton
            width="150px"
            height="34px"
            backgroundColor="green_haze"
            fontSize="14px"
            type="primary"
            onClick={this.onAddClick}
          >
            ADD NEW ITEM
            <Icon type="plus" />
          </CustomButton>

          <ChangeHistoriesButton
            width="wrap_content"
            height="wrap_content"
            title="Change History"
            table="SickLeave"
            caregiverId={Number(this.id)}
          />
        </FlexRowContainer>

        <DataTables
          width="match_parent"
          height="wrap_content"
          color="green_haze"
          fontSize="14px"
          size="small"
          isInsidePanel={false}
          withColumnFilter={false}
          columns={this.createColumns()}
          dataSource={this.createDataSource(sickLeaves)}
        />
      </FlexColContainer>
    );
  }
}

// https://facebook.github.io/react/docs/typechecking-with-proptypes.html
CaregiverSickLeaves.propTypes = propTypes;

export default Form.create()(CaregiverSickLeaves);
